import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import TranslateIcon from '@mui/icons-material/Translate';
import { COMMON_KEYS, NS } from '../constants/keys';
import { setAppLocale, SUPPORTED_LOCALES, SupportedLocale } from '../locale';

type LocaleSwitcherProps = {
  /** Navbar: header icon button on green bar. Inline: clean gray border for page content. */
  variant?: 'navbar' | 'inline';
};

type LanguageOption = {
  code: SupportedLocale;
  labelKey?: string;
  nativeName: string;
};

const LANGUAGES: LanguageOption[] = [
  { code: 'en', labelKey: COMMON_KEYS.locale.english, nativeName: 'English' },
  { code: 'id', labelKey: COMMON_KEYS.locale.indonesian, nativeName: 'Bahasa Indonesia' },
  { code: 'si', nativeName: 'සිංහල' },
  { code: 'ta', nativeName: 'தமிழ்' },
  { code: 'ru', nativeName: 'Русский' },
];

export const LocaleSwitcher: React.FC<LocaleSwitcherProps> = ({ variant = 'inline' }) => {
  const { t, i18n } = useTranslation(NS.common);
  const current = (SUPPORTED_LOCALES.includes(i18n.language as SupportedLocale)
    ? i18n.language
    : 'en') as SupportedLocale;
  const isNavbar = variant === 'navbar';
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (locale: SupportedLocale) => {
    setAppLocale(locale);
    handleClose();
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
      {/* Translate "文A" Header Icon Button */}
      <IconButton
        onClick={handleOpen}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={t(COMMON_KEYS.locale.switchLabel)}
        title="Change Language"
        size="small"
        sx={{
          borderRadius: '6px',
          p: 0.875,
          transition: 'all 0.2s ease',
          ...(isNavbar
            ? {
                color: 'var(--navbar-text, #ffffff)',
                '&:hover': {
                  bgcolor: 'var(--navbar-btn-hover, rgba(255, 255, 255, 0.2))',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }
            : {
                color: '#334155',
                bgcolor: '#ffffff',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                '&:hover': {
                  bgcolor: '#f8fafc',
                  borderColor: '#cbd5e1',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }),
        }}
      >
        <TranslateIcon sx={{ fontSize: 20 }} />
      </IconButton>

      {/* Popover Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 4,
          sx: {
            mt: 1,
            minWidth: 176,
            borderRadius: '6px',
            border: '1px solid #f1f5f9',
            py: 0.5,
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
          },
        }}
      >
        {LANGUAGES.map((lang) => {
          const isSelected = current === lang.code;
          const labelText = lang.labelKey ? t(lang.labelKey) : lang.nativeName;

          return (
            <MenuItem
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              selected={isSelected}
              sx={{
                mx: 0.5,
                my: 0.25,
                px: 1.5,
                py: 0.875,
                borderRadius: '6px',
                gap: 1,
                transition: 'all 0.15s ease',
                '&.Mui-selected': {
                  bgcolor: '#f1f5f9',
                  '&:hover': {
                    bgcolor: '#e2e8f0',
                  },
                },
                '&:hover': {
                  bgcolor: '#f8fafc',
                },
              }}
            >
              {/* Checkmark slot */}
              <ListItemIcon sx={{ minWidth: 20, color: '#0f172a' }}>
                {isSelected ? (
                  <CheckIcon sx={{ fontSize: 16 }} />
                ) : (
                  <Box sx={{ width: 16, height: 16 }} />
                )}
              </ListItemIcon>

              {/* Language Name */}
              <Typography
                noWrap
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: isSelected ? 600 : 500,
                  color: isSelected ? '#0f172a' : '#334155',
                }}
              >
                {labelText || lang.nativeName}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default LocaleSwitcher;
